import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as aiApi from "./ai-api";
import { aiKbQueries, aiQueries } from "./ai-keys";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
  AddAiMemberInput,
  AiAssistantKbItem,
  AiMember,
  AiPersona,
  AiPersonaUpdate,
  CreateAiPersonaInput,
  RemoveAiMemberInput,
  SharedWith,
} from "@shared/types/supabase.types";
import { containerQueries } from "@/features/containers";

export function useAiPersonasQuery(filters?: {
  sharedWith?: SharedWith;
  containerId?: string;
}): UseQueryResult<AiPersona[], Error> {
  return useQuery(aiQueries.list(filters));
}

export function useCreateAiPersonaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAiPersonaInput) => aiApi.createAiPersona(input),
    onSuccess: (created: AiPersona, variables) => {
      // 1) Update AI personas cache
      queryClient.setQueryData<AiPersona[]>(aiQueries.lists(), (prev) => {
        if (!prev) return [created];
        const exists = prev.some((p) => p.id === created.id);
        return exists ? prev : [created, ...prev];
      });

      // 2) Update container members cache for this container to make sure the AI assistant is set to active immediately after creation
      const membersKey = containerQueries.membersByContainer(variables.p_container_id, [
        "ai",
      ]).queryKey;

      queryClient.setQueryData<AiMember[]>(membersKey, (prev) => {
        const list = prev ?? [];
        if (list.some((aiMember) => aiMember.ai_persona_id === created.id)) return list;

        const now = new Date().toISOString();
        const optimisticMember: AiMember = {
          container_member_id: `optimistic_${crypto.randomUUID()}`,
          container_id: variables.p_container_id,
          role: "member",
          container_member_created_at: now,
          container_member_updated_at: now,
          ai_persona_id: created.id,
          actor_type: "ai",
          ai_persona_display_name: created.display_name,
          ai_persona_handle: created.handle,
          job_title: null,
          ai_persona_created_at: created.created_at,
          ai_persona_updated_at: created.updated_at,
          actor_id: `optimistic_${crypto.randomUUID()}`,
          ai_persona_avatar_url: null,
          ai_persona_description: null,
          ai_persona_model: null,
          ai_persona_system_prompt: null,
        };
        return [optimisticMember, ...list];
      });
    },
    onSettled: async (_created, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: aiQueries.lists() });
      await queryClient.invalidateQueries({
        queryKey: containerQueries.membersByContainer(variables.p_container_id, ["ai"]).queryKey,
      });
    },
  });
}

export function useAddAiMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddAiMemberInput) => aiApi.addAiMember(input),
    onSuccess: (added: AiMember, variables) => {
      // Update container members cache for this container to make sure the AI assistant is set to active immediately after creation
      const membersKey = containerQueries.membersByContainer(variables.p_container_id, [
        "ai",
      ]).queryKey;

      queryClient.setQueryData<AiMember[]>(membersKey, (prev) => {
        const list = prev ?? [];
        if (list.some((aiMember) => aiMember.container_member_id === added.container_member_id))
          return list;

        return [added, ...list];
      });
    },
    onSettled: async (_created, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: containerQueries.membersByContainer(variables.p_container_id, ["ai"]).queryKey,
      });
    },
  });
}

export function useRemoveAiMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RemoveAiMemberInput) => aiApi.removeAiMember(input),
    onSuccess: (_removed, variables) => {
      const membersKey = containerQueries.membersByContainer(variables.p_container_id, [
        "ai",
      ]).queryKey;

      queryClient.setQueryData<AiMember[]>(membersKey, (prev) => {
        const list = prev ?? [];
        return list.filter((aiMember) => aiMember.ai_persona_id !== variables.p_ai_persona_id);
      });
    },
    onSettled: async (_removed, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: containerQueries.membersByContainer(variables.p_container_id, ["ai"]).queryKey,
      });
    },
  });
}

export function useDeleteAiPersonaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => aiApi.deleteAiPersona(id),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: aiQueries.lists() });
      await queryClient.invalidateQueries({
        queryKey: containerQueries.memberships(),
      });
    },
  });
}

export function useUpdateAiPersonaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AiPersonaUpdate }) =>
      aiApi.updateAiPersona(id, input),
    onSuccess: (updated: AiPersona) => {
      queryClient.setQueryData<AiPersona[]>(aiQueries.lists(), (prev) => {
        if (!prev) return [updated];
        return prev.map((persona) => (persona.id === updated.id ? updated : persona));
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: aiQueries.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// AI Assistant Knowledge Base hooks
// ---------------------------------------------------------------------------

export function useAiAssistantKbItemsQuery(
  aiPersonaId: string | undefined,
  projectId?: string,
): UseQueryResult<AiAssistantKbItem[], Error> {
  const queryOptions = aiKbQueries.byPersona(aiPersonaId ?? "", projectId);
  return useQuery({
    ...queryOptions,
    enabled: Boolean(aiPersonaId),
    refetchInterval: (query) => {
      const items = query.state.data;
      if (!items) return false;
      const hasPending = items.some(
        (item) => item.parsing_status === "pending" || item.parsing_status === "processing",
      );
      return hasPending ? 3000 : false;
    },
  });
}

export function useUploadAiAssistantKbItemMutation(aiPersonaId: string, projectId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, actorId }: { file: File; actorId: string }) =>
      aiApi.uploadAiAssistantKbItem({
        aiPersonaId,
        file,
        actorId,
        projectId,
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: aiKbQueries.byPersona(aiPersonaId, projectId).queryKey,
      });
    },
  });
}

export function useDeleteAiAssistantKbItemMutation(aiPersonaId: string, projectId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => aiApi.deleteAiAssistantKbItem(id),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: aiKbQueries.byPersona(aiPersonaId, projectId).queryKey,
      });
    },
  });
}
